/**
 * B2: the react-query keys every role-template reader and invalidator shares
 * (the role editor page, the Roles list, the preview-bar editor, and
 * `FieldsProvider.invalidateFieldQueries` after a guest field changed).
 *
 * One definition each, so a field create/rename/delete reaches every copy:
 *
 * - `roleTemplatesKey` — the Roles list (full projection). PREFIX of the
 *   single-template keys: invalidating it also invalidates every
 *   `roleTemplateKey` of the event.
 * - `roleTemplateKey` — one template, read fresh by the role editor page.
 * - `rolePreviewScopeTemplateKey` — the preview-bar editor's admin read of the
 *   previewed template (bypasses the preview bearer). The 2-element form is
 *   the event prefix.
 * - `guestFieldCatalogueKey` — the loud guest field catalogue
 *   (`getGuestFieldCatalogue`). Lives under `["additionalFields", eventId]`,
 *   which `FieldsProvider.invalidateFieldQueries` already invalidates by
 *   prefix — so every field CRUD marks it stale with no extra line.
 */
export const roleTemplatesKey = (eventId: string) =>
  ["eventSettings", eventId, "roleTemplates"] as const;

export const roleTemplateKey = (eventId: string, templateId: string) =>
  ["eventSettings", eventId, "roleTemplates", "single", templateId] as const;

export const rolePreviewScopeTemplateKey = (eventId: string, templateId?: string) =>
  templateId === undefined
    ? (["rolePreviewScopeTemplate", eventId] as const)
    : (["rolePreviewScopeTemplate", eventId, templateId] as const);

export const guestFieldCatalogueKey = (eventId: string) =>
  ["additionalFields", eventId, "guestCatalogue"] as const;

/**
 * The preview-bar editor's admin read of the guest fields (`getFields` with
 * types — the row-filter editor needs them, the catalogue has none). A GUEST
 * field change marks it stale with the other role-template views.
 */
export const rolePreviewEditorFieldsKey = (eventId: string | null) =>
  ["rolePreviewEditorFields", eventId] as const;
